import React from 'react';
import PropTypes from 'prop-types';
import Typeface, { TypefaceInterface } from './index';
import { FontBase } from '../../const';
import theme from '../theme';

const baseConfig = {
  color: 'unset',
  fontFamily: theme.fontset.mosvita,
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 1,
  letterSpacing: 'unset',
  fontSize: FontBase
};

const types = {
  thin: {
    fontWeight: 100
  },
  thinItalic: {
    fontWeight: 100,
    fontStyle: 'italic'
  },
  extraLight: {
    fontWeight: 200
  },
  extraLightItalic: {
    fontWeight: 200,
    fontStyle: 'italic'
  },
  light: {
    fontWeight: 300
  },
  lightItalic: {
    fontWeight: 300,
    fontStyle: 'italic'
  },
  regularItalic: {
    fontStyle: 'italic'
  },
  semiBold: {
    fontWeight: 600
  },
  semiBoldItalic: {
    fontWeight: 600,
    fontStyle: 'italic'
  },
  bold: {
    fontWeight: 600
  },
  boldItalic: {
    fontWeight: 600,
    fontStyle: 'italic'
  },
  extraBold: {
    fontWeight: 800
  },
  extraBoldItalic: {
    fontWeight: 800,
    fontStyle: 'italic'
  },
  black: {
    fontWeight: 900
  },
  blackItalic: {
    fontWeight: 900,
    fontStyle: 'italic'
  },
  buttonText: {
    fontSize: 12,
    fontWeight: 600
  },
  menuHeader: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.colors.white
  },
  newsLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.colors.secondary
  },
  newsTitle: {
    fontSize: 16,
    lineHeight: '20px',
    fontWeight: 600,
    color: theme.colors.black
  },
  newsDate: {
    fontSize: 10,
    fontWeight: 400,
    color: theme.colors.black
  },
  detailIcon: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.grayLight
  },
  detailText: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.grayLight,
    textTransform: 'uppercase'
  },
  detailTextNumber: {
    fontSize: 30,
    fontWeight: 600,
    color: theme.colors.secondary
  },
  partnerTitleDesktop: {
    fontSize: 32,
    fontWeight: 600,
    letterSpacing: '-2px',
    color: theme.colors.black
  },
  partnerTitleMobile: {
    fontSize: 26,
    fontWeight: 600,
    letterSpacing: '-2px',
    color: theme.colors.black
  },
  partnerTitleQuote: {
    fontSize: 26,
    fontWeight: 600,
    letterSpacing: '-1px',
    color: theme.colors.black
  },
  partnerTextQuote: {
    fontStyle: 'italic',
    fontSize: 30,
    color: theme.colors.grayDark,
    fontWeight: 300
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 1,
    color: theme.colors.grayLight
  },
  planBoxTitle: {
    fontSize: 50,
    fontWeight: 600,
    letterSpacing: -0.2,
    color: theme.colors.white
  },
  planBoxTitleMobile: {
    fontSize: 40,
    fontWeight: 600,
    letterSpacing: -1,
    lineHeight: '50px',
    color: theme.colors.white
  },
  planBoxText: {
    fontSize: 18,
    fontWeight: 400,
    color: theme.colors.white,
    lineHeight: '30px'
  },
  planBoxLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.white,
    textTransform: 'uppercase',
    letterSpacing: 1
  },
  headerTitle: {
    fontSize: 50,
    fontWeight: 600,
    color: theme.colors.white,
    letterSpacing: '-2px'
  },
  headerTitleMobile: {
    fontSize: 40,
    fontWeight: 600,
    color: theme.colors.white,
    letterSpacing: '-2px'
  },
  headerEdition: {
    fontSize: 14,
    color: theme.colors.white,
    letterSpacing: 1
  },
  headerScroll: {
    fontSize: 14,
    color: theme.colors.white
  },
  headerText: {
    fontSize: 16,
    fontWeight: 300,
    lineHeight: '24px',
    letterSpacing: 0,
    color: theme.colors.white
  },
  cardTitle: {
    fontSize: 26,
    fontWeight: 600,
    letterSpacing: '-1px',
    lineHeight: '36px',
    color: theme.colors.black
  },
  cardTask: {
    fontSize: 12,
    color: theme.colors.grayLight,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    lineHeight: 1.2,
    fontWeight: 600
  },
  cardDescription: {
    fontSize: 16,
    color: theme.colors.black,
    fontWeight: 300,
    lineHeight: '24px'
  },
  cardLink: {
    fontSize: 14,
    color: theme.colors.primary,
    letterSpacing: '1px',
    lineHeight: '18px',
    fontWeight: 600
  },
  cardTestifier: {
    fontSize: 16,
    color: theme.colors.black,
    letterSpacing: '1px',
    fontWeight: 600,
    lineHeight: 1.5
  },
  subMenu: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.colors.secondary
  },
  subMenuTablet: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.colors.white
  },
  projectTitleColumn: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.colors.secondary
  },
  projectTextColumn: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '24px',
    color: theme.colors.gray
  },
  text: {
    fontSize: 16,
    fontWeight: 300,
    lineHeight: '24px',
    color: theme.colors.black
  },
  courseOnlineDetailTitle: {
    fontSize: 20,
    fontWeight: 600,
    lineHeight: '26px',
    color: theme.colors.black
  },
  courseOnlineDetailBody: {
    fontSize: 16,
    fontWeight: 300,
    lineHeight: '24px',
    color: theme.colors.gray
  },
  courseOnlineCardTitle: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '20px',
    color: theme.colors.black
  },
  courseOnlineCardBody: {
    fontSize: 16,
    fontWeight: 300,
    lineHeight: '24px',
    color: theme.colors.gray
  },
  courseOnlineFooterTitle: {
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '44px',
    letterSpacing: '-2px',
    color: theme.colors.black
  },
  courseOnlineFooterBody: {
    fontSize: 16,
    fontWeight: 300,
    lineHeight: '24px',
    letterSpacing: 0,
    color: theme.colors.black
  },
  sectionHeaderTitle: {
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '44px',
    letterSpacing: '-2px',
    color: theme.colors.black
  },
  sectionHeaderTitleMobile: {
    fontSize: 26,
    fontWeight: 600,
    lineHeight: '36px',
    letterSpacing: '-1px',
    color: theme.colors.black
  },
  sectionHeaderBody: {
    fontSize: 16,
    fontWeight: 300,
    lineHeight: '24px',
    color: theme.colors.black
  },
  classroomCourseSubtitle: {
    fontSize: 26,
    fontWeight: 600,
    lineHeight: '36px',
    letterSpacing: '-1px',
    color: theme.colors.black,
    textAlign: 'center'
  },
  classroomCourseSubtitlePhone: {
    fontSize: 20,
    fontWeight: 600,
    lineHeight: '36px',
    letterSpacing: '-1px',
    color: theme.colors.black,
    textAlign: 'center'
  },
  authorName: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '20px',
    letterSpacing: '1px',
    color: theme.colors.black
  },
  authorProfession: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '18px',
    letterSpacing: '1px',
    color: theme.colors.grayLighter
  },
  footerTitle: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: '18px',
    color: theme.colors.white
  },
  footerText: {
    fontSize: 11,
    color: theme.colors.white,
    lineHeight: '1.6'
  },
  partnerTitleCarousel: {
    fontSize: 26,
    fontWeight: 600,
    color: theme.colors.black,
    lineHeight: '34px',
    letterSpacing: '-1.27px'
  },
  viewMore: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.colors.primary,
    letterSpacing: '1px'
  },
  emptyArrayMessage: {
    fontSize: 26,
    fontWeight: 600,
    color: theme.colors.grayLight,
    textAlign: 'center'
  }
};

const Mosvita = ({ type, configuration, children, htmlAttribute, onClick }) => (
  <Typeface
    htmlAttribute={htmlAttribute}
    configuration={{ ...baseConfig, ...types[type], ...configuration }}
    onClick={onClick}
  >
    {children}
  </Typeface>
);

Mosvita.propTypes = {
  htmlAttribute: PropTypes.string,
  children: PropTypes.node,
  type: PropTypes.oneOf(Object.keys(types)),
  configuration: TypefaceInterface,
  onClick: PropTypes.func
};

export default Mosvita;
